// src/pages/Enseignants/PrepaPhysique/EditExerciseResultModal.jsx
import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { FaSave, FaTimes } from "react-icons/fa";
import Button from "../../../components/Button/Button";
import { toast } from "react-toastify";
import { updateAdherentExerciseResult } from "../../../services/prepaPhysiqueServices";

const blank = {
  adherent_id: "",
  prepa_physique_id: "",
  date_realisation: new Date().toISOString().slice(0, 10),
  repetitions: "",
  is_done: false,
};

const EditExerciseResultModal = ({ isOpen, onClose, result, refresh }) => {
  const [formData, setFormData] = useState(blank);
  const [saving, setSaving] = useState(false);
  const dialogRef = useRef(null);

  // Pré-remplissage
  useEffect(() => {
    if (!isOpen || !result) return;
    setFormData({
      adherent_id: result.adherent_id,
      prepa_physique_id: result.prepa_physique_id,
      date_realisation:
        result.date_realisation || new Date().toISOString().slice(0, 10),
      repetitions: result.repetitions ?? "",
      is_done: !!result.is_done,
    });
  }, [isOpen, result]);

  // ESC + focus + scroll lock
  useEffect(() => {
    if (!isOpen) return;

    const t = setTimeout(() => {
      dialogRef.current?.querySelector("input[name='date_realisation']")?.focus();
    }, 0);

    const onKey = (e) => e.key === "Escape" && handleClose();
    window.addEventListener("keydown", onKey);

    const root = document.documentElement;
    const prevOverflow = root.style.overflow;
    root.style.overflow = "hidden";

    return () => {
      clearTimeout(t);
      window.removeEventListener("keydown", onKey);
      root.style.overflow = prevOverflow;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleClose = () => {
    setFormData(blank);
    onClose?.();
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) handleClose();
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      await updateAdherentExerciseResult({
        ...formData,
        repetitions: formData.repetitions === "" ? null : Number(formData.repetitions),
      });
      toast.success("Résultat mis à jour !");
      await Promise.resolve(refresh?.());
      handleClose();
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors de la mise à jour.");
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen || !result) return null;

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 sm:p-6 bg-slate-900/60 backdrop-blur-sm"
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-label="Modifier l’exercice assigné"
    >
      <div
        ref={dialogRef}
        className="relative w-full max-w-[720px] flex flex-col overflow-hidden rounded-2xl shadow-2xl ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/95 dark:bg-slate-900/95 max-h-[92dvh]"
        style={{ WebkitOverflowScrolling: "touch" }}
      >
        {/* Header sticky */}
        <div className="sticky top-0 z-10 bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 px-5 py-4 flex items-center justify-between">
          <h3 className="text-white font-semibold">Modifier l’exercice assigné</h3>
          <Button
            text="Fermer"
            icon={FaTimes}
            onClick={handleClose}
            className="!rounded-xl bg-white/10 hover:bg-white/20 text-white gap-2"
          />
        </div>

        {/* Corps scrollable */}
        <form onSubmit={handleSubmit} className="grow overflow-y-auto p-5 space-y-4">
          {/* Infos fixes */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="rounded-xl px-3 py-2 bg-slate-50 dark:bg-slate-800/40 ring-1 ring-slate-200/70 dark:ring-slate-700/70 text-sm">
              <div className="opacity-60 text-xs">Adhérent</div>
              <div className="font-medium">
                {result?.Adherent?.nom?.toUpperCase()} {result?.Adherent?.prenom}
              </div>
            </div>
            <div className="rounded-xl px-3 py-2 bg-slate-50 dark:bg-slate-800/40 ring-1 ring-slate-200/70 dark:ring-slate-700/70 text-sm">
              <div className="opacity-60 text-xs">Exercice</div>
              <div className="font-medium">{result?.Exercice?.nom}</div>
            </div>
          </div>

          {/* Date & Répétitions */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs mb-1 opacity-70">Date de réalisation</label>
              <input
                type="date"
                name="date_realisation"
                value={formData.date_realisation}
                onChange={handleChange}
                required
                className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 text-slate-900 dark:text-slate-100 px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-xs mb-1 opacity-70">Répétitions</label>
              <input
                type="number"
                name="repetitions"
                min="0"
                placeholder="Nombre de répétitions"
                value={formData.repetitions}
                onChange={handleChange}
                required
                className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 text-slate-900 dark:text-slate-100 px-3 py-2"
              />
            </div>
          </div>

          {/* Statut */}
          <label className="inline-flex items-center gap-2 rounded-xl px-3 py-2 bg-slate-50 dark:bg-slate-800/40 ring-1 ring-slate-200/70 dark:ring-slate-700/70">
            <input
              type="checkbox"
              name="is_done"
              checked={formData.is_done}
              onChange={handleChange}
              className="h-5 w-5 accent-violet-500"
            />
            <span>Exercice effectué</span>
          </label>

          {/* Anti-recouvrement par le footer sticky */}
          <div className="h-12 sm:h-0" />
        </form>

        {/* Footer sticky */}
        <div className="sticky bottom-0 z-10 border-t border-slate-200 dark:border-slate-700 bg-white/90 dark:bg-slate-900/90 px-4 sm:px-6 py-3 flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-3">
          <Button
            text="Annuler"
            icon={FaTimes}
            onClick={handleClose}
            type="button"
            className="!rounded-xl !bg-slate-100 !text-slate-900 hover:!bg-slate-200 dark:!bg-slate-800 dark:!text-slate-100 dark:hover:!bg-slate-700 gap-2"
          />
          <Button
            text={saving ? "Enregistrement..." : "Enregistrer"}
            icon={FaSave}
            type="submit"
            disabled={saving}
            className="!rounded-xl gap-2"
            onClick={() => dialogRef.current?.querySelector("form")?.requestSubmit()}
          />
        </div>
      </div>

      {/* Styles locaux : scrollbar */}
      <style>{`
        .grow.overflow-y-auto::-webkit-scrollbar { width: 10px; }
        .grow.overflow-y-auto::-webkit-scrollbar-thumb {
          background: rgba(148,163,184,.5);
          border-radius: 9999px;
          border: 3px solid transparent;
          background-clip: content-box;
        }
        .dark .grow.overflow-y-auto::-webkit-scrollbar-thumb {
          background: rgba(148,163,184,.35);
        }
        .grow.overflow-y-auto::-webkit-scrollbar-track { background: transparent; }
      `}</style>
    </div>,
    document.body
  );
};

export default EditExerciseResultModal;
